import { useEffect, useRef } from 'react';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { useAuthStore } from '@/auth/auth-store';
import { createHubConnection } from '@/shared/realtime/signalr-connection';

/**
 * Encounter SignalR hub — Flutter parity:
 * `signalRHub(SignalRService.Encounter)` resolves to
 * `/api/patient-encounter/NotificationsHub`
 * (`scrips_core/lib/constants/api_constants.dart:528-530`).
 *
 * URL composition (Samer review PR #158 §3): the constant is a bare
 * path. `createHubConnection` passes it through `HubConnectionBuilder.withUrl()`
 * which resolves relative paths against `window.location.origin`. In
 * dev that's `http://localhost:7111` and Vite's proxy forwards to
 * `dev-api.scrips.com`; in prod the app and API are same-origin. Same
 * pattern as `BILLING_DHPO_HUB_URL` (`/api/EClaim/DHPO/RequestHub`)
 * which is in production traffic today, so URL composition is proven.
 * A one-character mismatch against the backend's `MapHub<>` registration
 * would 404 with no reconnect — verified path matches the Flutter
 * constant verbatim.
 *
 * The same hub carries multiple encounter-scoped events; the React
 * hook below subscribes only to `UpdateVitals`. Other listeners on
 * this hub belong to their respective screens (refresh encounter,
 * post-visit summary, etc.).
 */
export const ENCOUNTER_HUB_URL = '/api/patient-encounter/NotificationsHub';

/**
 * Payload for the `UpdateVitals` hub event. Flutter parity:
 * `RealTimeViatlsResponse` in `realtime_vitals_resp_model.dart:11-33`.
 * Note Flutter's misspelled class name (`Viatls`) — the wire shape
 * itself is correct (camelCase keys).
 */
export interface VitalsUpdateSignalRPayload {
  patientId?: string | null;
  encounterId?: string | null;
  appointmentId?: string | null;
}

interface UseVitalsRealtimeParams {
  /**
   * Fired when the Encounter hub emits `UpdateVitals` and the payload
   * matches the caller's appointmentId OR encounterId (mirrors Flutter's
   * filter at `vitals_tab.dart:152-153`). When unmatched, the event
   * is dropped — different appointments share the same hub.
   */
  onVitalsUpdate?: (payload: VitalsUpdateSignalRPayload) => void;
  /** Filter: only fire callback for this appointment. Required. */
  appointmentId: string | null | undefined;
  /**
   * PROD-1288 / ISO H-38: the patient whose SignalR group this connection
   * joins, so the now per-patient-group-targeted `UpdateVitals` reaches this
   * client. Without it the connection receives nothing post-cutover.
   */
  patientId?: string | null | undefined;
  /** Filter: only fire callback for this encounter. Optional — the
   *  callback also fires when appointmentId matches alone. */
  encounterId?: string | null | undefined;
  /**
   * Hook-level mid-edit safeguard (Samer review PR #158 §2). When
   * `true`, incoming `UpdateVitals` events are dropped before
   * reaching the caller's `onVitalsUpdate`. Pass `editing` from the
   * consumer's local state — keeps the mid-edit protection
   * enforceable inside the hook rather than left to each caller to
   * remember to short-circuit in their callback.
   *
   * Without this, a server-side refresh fired during a half-typed
   * form would clobber the user's in-progress values. Default:
   * `false` (events flow through).
   */
  paused?: boolean;
}

/**
 * useVitalsRealtime — subscribes to `UpdateVitals` on the Encounter
 * SignalR hub for the currently-viewed appointment / encounter (PROD-795
 * PR 5).
 *
 * Filter behavior (Flutter `vitals_tab.dart:148-167`):
 *   - Drop if neither appointmentId nor encounterId matches.
 *   - If appointmentId matches but encounterId is unset, still fire
 *     the callback — the caller may need to refetch the encounter
 *     first before reading vitals.
 *
 * **Why OR not AND** (Samer review PR #158 §1): in this app's data
 * model, an encounter belongs to exactly ONE appointment (the
 * encounter's `appointment` field links them). So `encounterId`
 * matching IMPLIES `appointmentId` matching in normal operation. The
 * OR is defensive for the narrow window where the appointment is
 * known but the encounter hasn't been created yet (PROD-797 Docs
 * create-on-demand surfaces this; vitals does too via PR 2's create-
 * encounter-on-save chain). A patient with multiple appointments
 * sharing one encounter would only exist if the backend data model
 * changed — Flutter still uses the OR, and the mid-edit safeguard
 * below catches any spurious-refresh scenario regardless.
 *
 * Lifecycle: connects on mount + auth, disconnects on unmount, on
 * logout (auth user goes null), or when the appointment/encounter
 * changes.
 *
 * **Cleanup ordering** (Samer review PR #158 §4): the cleanup fires
 * `hub.stop()` fire-and-forget (`void` + `.catch(() => {})`). Same
 * pattern as `use-billing-realtime.ts` (proven in production). When
 * appointmentId changes, the cleanup's stop() races briefly with the
 * new effect's start() — both connections coexist for the duration
 * of the negotiation handshake (~100ms). The OLD connection only
 * dispatches to the OLD `callbackRef` value via the closure-captured
 * `appointmentId`/`encounterId` filter, so a stray event during the
 * overlap window only fires if it matches BOTH the old and new
 * appointment context — vanishingly unlikely in practice. The
 * `paused` parameter (§2) is the belt-and-suspenders backup.
 *
 * The hook owns hub lifecycle. It does NOT mutate React Query cache —
 * the caller passes its own invalidation logic via `onVitalsUpdate`
 * so each consumer screen owns its own query keys.
 */
export function useVitalsRealtime({
  onVitalsUpdate,
  appointmentId,
  encounterId,
  patientId,
  paused = false,
}: UseVitalsRealtimeParams): void {
  const userId = useAuthStore((s) => s.user?.userId);

  // Stable callback ref so inline arrow functions don't reconnect the
  // hub on every render. Same pattern as use-billing-realtime.ts.
  const callbackRef = useRef(onVitalsUpdate);
  useEffect(() => {
    callbackRef.current = onVitalsUpdate;
  }, [onVitalsUpdate]);

  // Mid-edit safeguard ref. Tracked separately from the effect deps
  // so changing it doesn't trigger a hub reconnect (which would be
  // expensive). The event handler reads the current value at fire
  // time — so flipping `paused` to true mid-edit immediately drops
  // any in-flight events.
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    if (!userId || !appointmentId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    // PROD-1288 / ISO H-38: hub auto-joins the per-patient group from this query param
    // (server-side OnConnectedAsync) — no `.invoke()` needed; Flutter works unchanged.
    const hub = createHubConnection(
      patientId
        ? `${ENCOUNTER_HUB_URL}?patientId=${encodeURIComponent(patientId)}`
        : ENCOUNTER_HUB_URL,
      getToken,
    );

    hub.on('UpdateVitals', (...args: unknown[]) => {
      // Hook-enforced mid-edit guard (Samer §2). Drop the event
      // BEFORE invoking the caller's callback — even if the caller
      // forgets to short-circuit in their handler, in-progress edits
      // stay safe.
      if (pausedRef.current) return;

      const payload = firstPayload(args);
      if (!payload) return;
      // Filter: skip events for other appointments on this hub.
      // Flutter parity: matches on appointmentId OR encounterId.
      const matchesAppt =
        payload.appointmentId != null &&
        payload.appointmentId === appointmentId;
      const matchesEnc =
        encounterId != null &&
        payload.encounterId != null &&
        payload.encounterId === encounterId;
      if (matchesAppt || matchesEnc) {
        callbackRef.current?.(payload);
      }
    });

    let disposed = false;
    const startSafely = async (conn: HubConnection): Promise<void> => {
      try {
        if (conn.state === HubConnectionState.Disconnected) {
          await conn.start();
        }
        if (disposed && conn.state !== HubConnectionState.Disconnected) {
          await conn.stop();
        }
      } catch {
        // Auto-reconnect handles real failures; StrictMode-induced
        // aborts are expected and we don't surface them.
      }
    };
    void startSafely(hub);

    return () => {
      disposed = true;
      if (hub.state === HubConnectionState.Connected) {
        void hub.stop().catch(() => {});
      }
    };
  }, [userId, appointmentId, encounterId, patientId]);
}

function firstPayload(args: unknown[]): VitalsUpdateSignalRPayload | undefined {
  const head = args[0];
  if (Array.isArray(head)) return head[0] as VitalsUpdateSignalRPayload | undefined;
  if (head && typeof head === 'object') return head as VitalsUpdateSignalRPayload;
  return undefined;
}
